import * as React from 'react'
import type {
  TSubmitMajorActionResponse,
  TSubmitMinorActionResponse,
} from '@kapitalist/contract/endpoints'
import type {TRuleset} from '@kapitalist/contract/ruleset'
import {unreachableCase} from '@kapitalist/utils/exhaustive'
import type {NEList} from '@kapitalist/utils/NEList'
import * as S from '@kapitalist/utils/Set'
import {isInstance} from '@kapitalist/utils/types'
import {JustSayNoCard} from '~/models/game/Cards/ActionCard'
import type GameData from '~/models/game/GameData'
import type GamePlayer from '~/models/game/GamePlayer'
import type MajorAction from '~/models/game/MajorAction'
import type PastGamePosition from '~/models/game/PastGamePosition'
import type {AnyCard, AnyMinorAction} from '~/models/game/types'
import CardInspectionModal from '~/pages/GameLobbyV2/CardInspectionModal'
import type {TClickableCardStateV2} from '~/pages/GameLobbyV2/clickableCardState'
import {maybeMutateClickableCardState} from '~/pages/GameLobbyV2/clickableCardState'
import GameBoardV2UI from '~/pages/GameLobbyV2/GameBoard/stateless'
import GameLog from '~/pages/GameLobbyV2/GameLog/stateless'
import HandDrawerUI from '~/pages/GameLobbyV2/HandDrawer/stateless'
import type {SettingsState} from '~/redux/reducers/settings'
import styles from './styles.module.css'

interface IProps {
  gameData: GameData
  settingsState: SettingsState

  chooseMajorAction: (
    majorAction: MajorAction
  ) => Promise<TSubmitMajorActionResponse>
  chooseMajorActionSubmitSuccess: (response: TSubmitMajorActionResponse) => void
  chooseMinorAction: (
    minorAction: AnyMinorAction
  ) => Promise<TSubmitMinorActionResponse>
  chooseMinorActionSubmitSuccess: (response: TSubmitMinorActionResponse) => void

  allPastGamePositions: NEList<PastGamePosition> | undefined

  rules: TRuleset
}

interface IState {
  submittingTableMajorAction: boolean
  submittingMinorActionReply: boolean

  // for getDerivedStateFromProps
  lastKnownGameDataHash: string

  // moved up from CurrentPlayerHand since we want to read it for IClickableCardStateV2PlaySlyDeal
  currentPlayerHandSelectedCardId?: string

  clickableCardState: TClickableCardStateV2
}

export default class GameLobbyV2UI extends React.Component<IProps, IState> {
  state: IState = {
    submittingTableMajorAction: false,
    submittingMinorActionReply: false,
    lastKnownGameDataHash: '',
    // fixed in getDerivedStateFromProps
    clickableCardState: {
      mode: 'none',
      inProgressMajorAction:
        this.props.gameData.playerGamePosition?.inProgressMajorAction,
      pendingMinorActionForMe: undefined,
      rules: this.props.rules,
    },
  }

  static getDerivedStateFromProps(
    nextProps: IProps,
    prevState: IState
  ): Partial<IState> | null {
    if (nextProps.gameData.hash === prevState.lastKnownGameDataHash) {
      // Not even sure we need to do this effort-saving early return tbh, but i'l leave it for now
      // console.log('skipping clickableCardState update b/c hash is equal', {
      //   nextProps,
      //   prevState,
      //   newTime: nextProps.gameData.currentGamePosition.time,
      //   oldTime: prevState.propTime,
      // })
      return null
    }

    let returnVal: Partial<IState> = {
      lastKnownGameDataHash: nextProps.gameData.hash,
    }

    const clickableCardState = maybeMutateClickableCardState(
      nextProps.gameData,
      nextProps.rules,
      prevState.clickableCardState
    )

    return {
      ...returnVal,
      clickableCardState,
    }
  }

  chooseMinorAction = (
    minorAction: AnyMinorAction
  ): Promise<TSubmitMinorActionResponse> => {
    const {chooseMinorAction} = this.props

    this.setState({submittingMinorActionReply: true})

    // detect errors here, but also pass them on
    const promise = chooseMinorAction(minorAction)
    promise
      .catch(e => {
        console.error('error submitting minor action: ', e)
      })
      .finally(() => this.setState({submittingMinorActionReply: false}))
      .catch(() => undefined)

    return promise
  }

  cardClicked = (card: AnyCard) => {
    const {clickableCardState: currentClickableCardState} = this.state

    switch (currentClickableCardState.mode) {
      case 'paySomething':
        this.setState(prevState => {
          if (prevState.clickableCardState.mode !== 'paySomething') {
            console.error(
              'clickableCardState mode changed from paySomething before setState could modify it',
              prevState,
              currentClickableCardState
            )
            return null
          }

          // make going back and forth between your JSN and clicking valuable cards easier
          // by deselecting all the other irrelevant things automatically
          const cardsToAutoDeselect =
            card instanceof JustSayNoCard
              ? prevState.clickableCardState.selectedCardIds
              : new Set(
                  this.props.gameData.playerGamePosition?.currentPlayerHand.cards
                    .filter(isInstance(JustSayNoCard))
                    .map(c => c.id)
                )

          return {
            clickableCardState: {
              ...prevState.clickableCardState,
              selectedCardIds: S.difference(
                S.toggleMembership(
                  card.id,
                  prevState.clickableCardState.selectedCardIds
                ),
                cardsToAutoDeselect
              ),
            },
          }
        })
        break
      case 'awaitingYourMajorAction':
        this.setState(prevState => {
          if (prevState.clickableCardState.mode !== 'awaitingYourMajorAction') {
            console.error(
              'clickableCardState mode changed from awaitingYourMajorAction before setState could modify it',
              prevState,
              currentClickableCardState
            )
            return null
          }

          // since only one can be selected at a time, we first check
          // which place the selected card is, and then toggle it's selected state in that correct place
          const selectedCardInHandId =
            prevState.clickableCardState.clickableCardsInHandIds.has(card.id)
              ? prevState.clickableCardState.selectedCardInHandId === card.id
                ? undefined
                : card.id
              : undefined
          const selectedCardInMyPortfolioId =
            prevState.clickableCardState.clickableCardsInMyPortfolioIds.has(card.id)
              ? prevState.clickableCardState.selectedCardInMyPortfolioId === card.id
                ? undefined
                : card.id
              : undefined

          switch (prevState.clickableCardState.consideringMajorAction?.type) {
            case 'slyDeal':
              if (
                prevState.clickableCardState.clickableCardsInHandIds.has(card.id)
              ) {
                // clicked a card in our hand.
                return {
                  clickableCardState: {
                    ...prevState.clickableCardState,
                    consideringMajorAction: undefined,
                    selectedCardInHandId,
                    selectedCardInMyPortfolioId: undefined,
                  },
                }
              } else if (
                prevState.clickableCardState.consideringMajorAction.requestableCardIds.has(
                  card.id
                )
              ) {
                return {
                  clickableCardState: {
                    ...prevState.clickableCardState,
                    consideringMajorAction: {
                      ...prevState.clickableCardState.consideringMajorAction,
                      selectedCardToRequestId: card.id,
                    },
                  },
                }
              }
              console.log('nothing to do with a card click', {
                card,
                currentClickableCardState,
                prevState,
              })
              break
            case 'forcedDeal':
              if (
                prevState.clickableCardState.clickableCardsInHandIds.has(card.id)
              ) {
                // clicked a card in our hand.
                return {
                  clickableCardState: {
                    ...prevState.clickableCardState,
                    consideringMajorAction: undefined,
                    selectedCardInHandId,
                    selectedCardInMyPortfolioId: undefined,
                  },
                }
              } else if (
                prevState.clickableCardState.consideringMajorAction.requestableCardIds.has(
                  card.id
                )
              ) {
                return {
                  clickableCardState: {
                    ...prevState.clickableCardState,
                    consideringMajorAction: {
                      ...prevState.clickableCardState.consideringMajorAction,
                      selectedCardToRequestId: card.id,
                    },
                  },
                }
              } else if (
                prevState.clickableCardState.consideringMajorAction.offerableCardIds.has(
                  card.id
                )
              ) {
                return {
                  clickableCardState: {
                    ...prevState.clickableCardState,
                    consideringMajorAction: {
                      ...prevState.clickableCardState.consideringMajorAction,
                      selectedCardToOfferId: card.id,
                    },
                  },
                }
              }
              console.log('nothing to do with a card click', {
                card,
                currentClickableCardState,
                prevState,
              })

              break
            case 'discard':
              if (
                prevState.clickableCardState.clickableCardsInHandIds.has(card.id)
              ) {
                return {
                  clickableCardState: {
                    ...prevState.clickableCardState,
                    consideringMajorAction: {
                      ...prevState.clickableCardState.consideringMajorAction,
                      selectedCardsToDiscardIds: S.toggleMembership(
                        card.id,
                        prevState.clickableCardState.consideringMajorAction
                          .selectedCardsToDiscardIds
                      ),
                    },
                  },
                }
              } else if (
                prevState.clickableCardState.clickableCardsInMyPortfolioIds.has(
                  card.id
                )
              ) {
                return {
                  clickableCardState: {
                    ...prevState.clickableCardState,
                    selectedCardInMyPortfolioId,
                  },
                }
              }
              console.log('nothing to do with a card click', {
                card,
                currentClickableCardState,
                prevState,
              })
              break
            case 'needsNeighborhood':
              // clicked a card in our hand (SpecialPropertyCard, UniversalRent, DealBreaker)
              // or on the table (SpecialPropertyCard)
              return {
                clickableCardState: {
                  ...prevState.clickableCardState,
                  consideringMajorAction: undefined,
                  selectedCardInHandId,
                  selectedCardInMyPortfolioId,
                },
              }
            case 'debtCollector':
              // there aren't any other cards to click to engage a debt collector
              break
            case 'directionalRent':
              /* eslint-disable no-debugger */
              debugger
              break
            case undefined:
              // select the action card we clicked in our hand
              break
            default:
              return unreachableCase(
                prevState.clickableCardState.consideringMajorAction
              )
          }

          return {
            clickableCardState: {
              ...prevState.clickableCardState,
              selectedCardInHandId,
              selectedCardInMyPortfolioId,
            },
          }
        })
        break
      case 'acceptOrJustSayNo':
        this.setState(prevState => {
          if (prevState.clickableCardState.mode !== 'acceptOrJustSayNo') {
            console.error(
              'clickableCardState mode changed from acceptOrJustSayNo before setState could modify it',
              prevState,
              currentClickableCardState
            )
            return null
          }

          return {
            clickableCardState: {
              ...prevState.clickableCardState,
              selectedCardInHandId:
                prevState.clickableCardState.selectedCardInHandId === card.id
                  ? undefined
                  : card.id,
            },
          }
        })
        break
      case 'chooseNeighborhood':
      case 'none':
        return
      default:
        return unreachableCase(currentClickableCardState)
    }
  }

  neighborhoodClicked = (neighborhoodId: string): void => {
    const {clickableCardState: currentClickableCardState} = this.state

    switch (currentClickableCardState.mode) {
      case 'awaitingYourMajorAction':
        this.setState(prevState => {
          if (prevState.clickableCardState.mode !== 'awaitingYourMajorAction') {
            console.error(
              'clickableCardState mode changed from awaitingYourMajorAction before setState could modify it',
              prevState,
              currentClickableCardState
            )
            return null
          }

          switch (prevState.clickableCardState.consideringMajorAction?.type) {
            case 'needsNeighborhood':
            case 'directionalRent':
              return {
                clickableCardState: {
                  ...prevState.clickableCardState,
                  consideringMajorAction: {
                    ...prevState.clickableCardState.consideringMajorAction,
                    selectedPortfolioNeighborhoodId: neighborhoodId,
                  },
                },
              }
            case 'slyDeal':
            case 'forcedDeal':
            case 'discard':
            case 'debtCollector':
            case undefined:
              // there aren't any other players to click to engage these
              return null
            default:
              return unreachableCase(
                prevState.clickableCardState.consideringMajorAction
              )
          }
        })
        return
      case 'chooseNeighborhood':
        this.setState(prevState => ({
          clickableCardState: {
            ...prevState.clickableCardState,
            selectedPortfolioNeighborhoodId: neighborhoodId,
          },
        }))
        return
      case 'paySomething':
      case 'acceptOrJustSayNo':
      case 'none':
        return
      default:
        return unreachableCase(currentClickableCardState)
    }
  }

  playerClicked = (player: GamePlayer): void => {
    const {clickableCardState: currentClickableCardState} = this.state

    switch (currentClickableCardState.mode) {
      case 'awaitingYourMajorAction':
        this.setState(prevState => {
          if (prevState.clickableCardState.mode !== 'awaitingYourMajorAction') {
            console.error(
              'clickableCardState mode changed from awaitingYourMajorAction before setState could modify it',
              prevState,
              currentClickableCardState
            )
            return null
          }

          switch (prevState.clickableCardState.consideringMajorAction?.type) {
            case 'slyDeal':
            case 'forcedDeal':
            case 'needsNeighborhood':
            case 'discard':
            case undefined:
              // there aren't any other players to click to engage these
              return null
            case 'debtCollector':
            case 'directionalRent':
              return {
                clickableCardState: {
                  ...prevState.clickableCardState,
                  consideringMajorAction: {
                    ...prevState.clickableCardState.consideringMajorAction,
                    selectedPlayerId:
                      prevState.clickableCardState.consideringMajorAction
                        .selectedPlayerId === player.id
                        ? undefined
                        : player.id,
                  },
                },
              }
            default:
              return unreachableCase(
                prevState.clickableCardState.consideringMajorAction
              )
          }
        })
        return
      case 'paySomething':
      case 'acceptOrJustSayNo':
      case 'chooseNeighborhood':
      case 'none':
        return
      default:
        return unreachableCase(currentClickableCardState)
    }
  }

  render() {
    const {clickableCardState} = this.state
    const {skin, gameLobbySize} = this.props.settingsState

    return (
      <div className={styles.gameLobbyWrapper}>
        <GameBoardV2UI
          {...this.props}
          skin={skin}
          gameLobbySize={gameLobbySize}
          chooseMajorActionSubmitSuccess={response => {
            this.props.chooseMajorActionSubmitSuccess(response)
            this.setState(prevState => {
              if (prevState.clickableCardState.mode === 'awaitingYourMajorAction') {
                // we just completed a major action, so we can safely clear the state that was tracking the action
                // we were just considering
                return {
                  clickableCardState: maybeMutateClickableCardState(
                    this.props.gameData,
                    this.props.rules,
                    {
                      ...prevState.clickableCardState,
                      consideringMajorAction: undefined,
                      selectedCardInHandId: undefined,
                      selectedCardInMyPortfolioId: undefined,
                    }
                  ),
                }
              }
              return null
            })
          }}
          clickableCardState={clickableCardState}
          onCardClicked={this.cardClicked}
          onPlayerClicked={this.playerClicked}
          onNeighborhoodClicked={this.neighborhoodClicked}
          setClickableCardState={setFunc => {
            this.setState(prevState => {
              const mutatedClickableCardState = maybeMutateClickableCardState(
                this.props.gameData,
                this.props.rules,
                setFunc(prevState.clickableCardState)
              )
              return {
                clickableCardState: mutatedClickableCardState,
              }
            })
          }}
        />

        <HandDrawerUI
          {...this.props}
          skin={skin}
          className={styles.handDrawer}
          clickableCardState={clickableCardState}
          onCardClicked={this.cardClicked}
        />

        {this.props.settingsState.betaFeatures.gameLog && (
          <GameLog
            skin={skin}
            pastGamePositions={this.props.allPastGamePositions}
            className={styles.gameLog}
          />
        )}

        <CardInspectionModal />
      </div>
    )
  }
}
